import React, { CSSProperties, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import {
    AlertCircle,
    ArrowLeft,
    ChevronDown,
    ChevronUp,
    FileWarning,
    FolderKanban,
    Loader,
    Sparkles,
    Trash2,
    Users,
} from 'lucide-react';
import RefreshButton from '../../components/main/RefreshButton/RefreshButton';
import { ticksToDate } from '../../Utils';

const GET_UNAPPROVED_CONTENT_SUMMARIES = gql`
    query getUnapprovedContentSummaries($projectId: String, $applicationName: String, $fromDay: String, $toDay: String) {
        UnapprovedContentSummaries(projectId: $projectId, applicationName: $applicationName, fromDay: $fromDay, toDay: $toDay) {
            id
            projectId
            projectName
            applicationName
            modelId
            modelName
            familyName
            day
            reporterId
            reporterName
            detectionCount
            firstSeenAt
            lastSeenAt
        }
    }
`;

const CLEANUP_LEGACY_UNAPPROVED_CONTENT = gql`
    mutation cleanupLegacyUnapprovedContent {
        CleanupLegacyUnapprovedContent {
            projectsScanned
            projectsChanged
            entriesMatched
            entriesRemoved
            failures
        }
    }
`;

type PeriodId = 'week' | 'month' | 'quarter';
type EnterpriseGroupBy = 'project' | 'user';
type TrendingGroupBy = 'family' | 'user';
type TrendingMode = 'latest' | 'history';

type SummaryRow = {
    id: string;
    projectId: string;
    projectName: string;
    applicationName: string;
    modelId: string;
    modelName: string;
    familyName: string;
    day: string;
    reporterId: string;
    reporterName: string;
    detectionCount: number;
    firstSeenAt: number;
    lastSeenAt: number;
};

type CleanupResult = {
    projectsScanned: number;
    projectsChanged: number;
    entriesMatched: number;
    entriesRemoved: number;
    failures: number;
};

const PERIODS: { id: PeriodId; label: string; days: number }[] = [
    { id: 'week', label: '1 week', days: 7 },
    { id: 'month', label: '1 month', days: 30 },
    { id: 'quarter', label: '3 months', days: 90 },
];

const firstDay = (days: number) => {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - Math.max(0, days - 1));
    return date.toISOString().slice(0, 10);
};

const formatSeen = (value: number) => value ? ticksToDate(String(value)) : '—';

const PeriodSelector = ({ value, onChange }: { value: PeriodId; onChange: (value: PeriodId) => void }) => (
    <div className="flex items-center gap-2">
        <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>Period:</span>
        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-default)' }}>
            {PERIODS.map(period => (
                <button
                    key={period.id}
                    onClick={() => onChange(period.id)}
                    className="px-3 py-1.5 text-xs font-medium transition-colors"
                    style={{
                        backgroundColor: value === period.id ? 'var(--accent)' : 'var(--bg-card)',
                        color: value === period.id ? '#fff' : 'var(--text-secondary)',
                        border: 'none',
                        cursor: 'pointer',
                    }}
                >
                    {period.label}
                </button>
            ))}
        </div>
    </div>
);

const ToggleSelector = <T extends string>({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: T;
    options: { id: T; label: string }[];
    onChange: (value: T) => void;
}) => (
    <div className="flex items-center gap-2">
        <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>{label}:</span>
        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-default)' }}>
            {options.map(option => (
                <button
                    key={option.id}
                    onClick={() => onChange(option.id)}
                    className="px-3 py-1.5 text-xs font-medium transition-colors"
                    style={{
                        backgroundColor: value === option.id ? 'var(--accent)' : 'var(--bg-card)',
                        color: value === option.id ? '#fff' : 'var(--text-secondary)',
                        border: 'none',
                        cursor: 'pointer',
                    }}
                >
                    {option.label}
                </button>
            ))}
        </div>
    </div>
);

const BackHeading = ({ title, subtitle, onBack }: { title: string; subtitle: string; onBack: () => void }) => (
    <div>
        <button
            onClick={onBack}
            className="flex items-center gap-1 text-xs font-medium mb-2 transition-colors"
            style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
            <ArrowLeft size={14} /> Back to Reports
        </button>
        <h1 className="page-title">{title}</h1>
        <p className="page-subtitle">{subtitle}</p>
    </div>
);

const LoadingCard = () => (
    <div className="card p-8 flex flex-col items-center justify-center gap-3 animate-slide-up">
        <Loader size={28} className="animate-spin" style={{ color: 'var(--accent)' }} />
        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Loading summaries…</div>
    </div>
);

const ErrorCard = ({ message }: { message: string }) => (
    <div className="card p-4 flex items-center gap-2" style={{ color: 'var(--danger)' }}>
        <AlertCircle size={18} />
        <span className="text-sm">{message}</span>
    </div>
);

const DetailTable = ({ rows, showProject }: { rows: SummaryRow[]; showProject?: boolean }) => {
    const details = useMemo(() => {
        const map = new Map<string, SummaryRow>();
        for (const row of rows) {
            const key = [row.projectId, row.applicationName, row.modelId || row.modelName, row.familyName, row.reporterId].join('|');
            const existing = map.get(key);
            if (!existing) map.set(key, { ...row });
            else {
                existing.detectionCount += row.detectionCount;
                existing.firstSeenAt = Math.min(existing.firstSeenAt, row.firstSeenAt);
                existing.lastSeenAt = Math.max(existing.lastSeenAt, row.lastSeenAt);
            }
        }
        return Array.from(map.values()).sort((a, b) => b.detectionCount - a.detectionCount);
    }, [rows]);

    return (
        <div className="overflow-auto">
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.75rem' }}>
                <thead>
                    <tr style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-default)' }}>
                        {showProject && <th className="text-left px-3 py-2">Project</th>}
                        <th className="text-left px-3 py-2">Application</th>
                        <th className="text-left px-3 py-2">Model</th>
                        <th className="text-left px-3 py-2">Family</th>
                        <th className="text-left px-3 py-2">User</th>
                        <th className="text-right px-3 py-2">Occurrences</th>
                        <th className="text-left px-3 py-2">First seen</th>
                        <th className="text-left px-3 py-2">Last seen</th>
                    </tr>
                </thead>
                <tbody>
                    {details.map(row => (
                        <tr key={`${row.id}-${row.projectId}-${row.familyName}`} style={{ borderBottom: '1px solid var(--border-default)' }}>
                            {showProject && (
                                <td className="px-3 py-2">
                                    <Link to={`/projects/${row.projectId}`} style={{ color: 'var(--accent-text)', textDecoration: 'none' }}>{row.projectName}</Link>
                                </td>
                            )}
                            <td className="px-3 py-2">{row.applicationName || '—'}</td>
                            <td className="px-3 py-2">{row.modelName || '—'}</td>
                            <td className="px-3 py-2">{row.familyName || '—'}</td>
                            <td className="px-3 py-2">{row.reporterName || row.reporterId || '—'}</td>
                            <td className="px-3 py-2 text-right font-semibold">{row.detectionCount.toLocaleString()}</td>
                            <td className="px-3 py-2">{formatSeen(row.firstSeenAt)}</td>
                            <td className="px-3 py-2">{formatSeen(row.lastSeenAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export const UnapprovedContentSummaryReport = ({ style, onBack }: { style?: CSSProperties; onBack: () => void }) => {
    const [periodId, setPeriodId] = useState<PeriodId>('month');
    const [groupBy, setGroupBy] = useState<EnterpriseGroupBy>('project');
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [cleanupResult, setCleanupResult] = useState<CleanupResult | null>(null);
    const period = PERIODS.find(item => item.id === periodId)!;
    const variables = useMemo(() => ({ fromDay: firstDay(period.days) }), [period.days]);
    const { data, loading, error, refetch } = useQuery(GET_UNAPPROVED_CONTENT_SUMMARIES, { variables });
    const [cleanup, cleanupState] = useMutation(CLEANUP_LEGACY_UNAPPROVED_CONTENT);
    const rows = useMemo(
        () => (data?.UnapprovedContentSummaries || []) as SummaryRow[],
        [data],
    );

    const groups = useMemo(() => {
        const map = new Map<string, { key: string; label: string; rows: SummaryRow[]; count: number }>();
        for (const row of rows) {
            const key = groupBy === 'project' ? row.projectId : (row.reporterId || row.reporterName || 'unknown');
            const label = groupBy === 'project' ? row.projectName : (row.reporterName || row.reporterId || '(unknown user)');
            const current = map.get(key) || { key, label, rows: [], count: 0 };
            current.rows.push(row);
            current.count += row.detectionCount;
            map.set(key, current);
        }
        return Array.from(map.values()).sort((a, b) => b.count - a.count);
    }, [rows, groupBy]);

    const totals = useMemo(() => ({
        detections: rows.reduce((sum, row) => sum + row.detectionCount, 0),
        projects: new Set(rows.map(row => row.projectId)).size,
        users: new Set(rows.map(row => row.reporterId || row.reporterName)).size,
    }), [rows]);

    const runCleanup = async () => {
        if (!window.confirm('Delete all legacy “Unapproved Content” dynamic-data entries and their blobs? New summary data will not be affected.')) return;
        setCleanupResult(null);
        try {
            const response = await cleanup();
            setCleanupResult(response.data?.CleanupLegacyUnapprovedContent || null);
        } catch {
            // Apollo exposes the error through cleanupState.error below.
        }
    };

    const toggle = (key: string) => setExpanded(previous => {
        const next = new Set(previous);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    return (
        <div className="page-container" style={{ ...style, overflow: 'auto' }} data-testid="UnapprovedContentReport">
            <div className="page-header">
                <BackHeading title="Unapproved Content" subtitle="Enterprise rollup from daily summary data" onBack={onBack} />
                <div className="flex items-center gap-3 flex-wrap">
                    <PeriodSelector value={periodId} onChange={setPeriodId} />
                    <ToggleSelector label="Group by" value={groupBy} onChange={setGroupBy} options={[
                        { id: 'project', label: 'Project' },
                        { id: 'user', label: 'User' },
                    ]} />
                    <RefreshButton onRefresh={refetch} className="btn-secondary" iconSize={16} />
                    <button
                        onClick={runCleanup}
                        disabled={cleanupState.loading}
                        className="btn-secondary flex items-center gap-1.5 text-xs"
                        style={{ color: 'var(--danger)', cursor: cleanupState.loading ? 'not-allowed' : 'pointer' }}
                        title="Temporarily available: remove legacy dynamic-data reports and blobs"
                    >
                        {cleanupState.loading ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        Clean up legacy data
                    </button>
                </div>
            </div>

            {cleanupResult && (
                <div className="card p-4 text-sm" style={{ borderLeft: `4px solid ${cleanupResult.failures ? 'var(--warning)' : 'var(--success)'}` }}>
                    {cleanupResult.entriesMatched === 0 ? (
                        <>No legacy Unapproved Content entries were found.</>
                    ) : (
                        <>
                            Removed <strong>{cleanupResult.entriesRemoved.toLocaleString()}</strong> legacy entries from{' '}
                            <strong>{cleanupResult.projectsChanged.toLocaleString()}</strong> projects.
                            {cleanupResult.failures > 0 && <> <strong>{cleanupResult.failures.toLocaleString()}</strong> entries failed and can be retried.</>}
                        </>
                    )}
                </div>
            )}
            {cleanupState.error && <ErrorCard message={cleanupState.error.message} />}
            {loading && <LoadingCard />}
            {error && <ErrorCard message={error.message} />}

            {!loading && !error && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-slide-up">
                        {[
                            { label: 'Occurrences', value: totals.detections, icon: FileWarning, colour: 'var(--danger)', background: 'var(--danger-soft, #fef2f2)' },
                            { label: 'Projects Affected', value: totals.projects, icon: FolderKanban, colour: 'var(--success)', background: 'var(--success-soft)' },
                            { label: 'Reporting Users', value: totals.users, icon: Users, colour: 'var(--info)', background: 'var(--info-soft)' },
                        ].map(card => (
                            <div className="card p-5" key={card.label}>
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: card.background }}>
                                    <card.icon size={20} style={{ color: card.colour }} />
                                </div>
                                <div className="stat-value">{card.value.toLocaleString()}</div>
                                <div className="stat-label mt-1">{card.label}</div>
                            </div>
                        ))}
                    </div>

                    <div className="card animate-slide-up">
                        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-default)' }}>
                            <h2 className="section-title" style={{ margin: 0 }}>By {groupBy === 'project' ? 'Project' : 'User'}</h2>
                        </div>
                        {groups.length === 0 ? (
                            <div className="text-sm text-center py-10" style={{ color: 'var(--text-tertiary)' }}>
                                No summary data has been collected in this period.
                            </div>
                        ) : groups.map(group => {
                            const isExpanded = expanded.has(group.key);
                            return (
                                <div key={group.key} style={{ borderBottom: '1px solid var(--border-default)' }}>
                                    <button
                                        onClick={() => toggle(group.key)}
                                        className="w-full flex items-center gap-3 px-6 py-3 text-left"
                                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                    >
                                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        <span className="flex-1 text-sm font-medium">{group.label}</span>
                                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}>
                                            {group.count.toLocaleString()} occurrences
                                        </span>
                                    </button>
                                    {isExpanded && <DetailTable rows={group.rows} showProject={groupBy === 'user'} />}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

export const TrendingUnapprovedContentSummaryReport = ({ style, onBack }: { style?: CSSProperties; onBack: () => void }) => {
    const [periodId, setPeriodId] = useState<PeriodId>('month');
    const [mode, setMode] = useState<TrendingMode>('history');
    const [groupBy, setGroupBy] = useState<TrendingGroupBy>('family');
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const period = PERIODS.find(item => item.id === periodId)!;
    const variables = useMemo(() => ({ fromDay: firstDay(period.days) }), [period.days]);
    const { data, loading, error, refetch } = useQuery(GET_UNAPPROVED_CONTENT_SUMMARIES, { variables });
    const allRows = useMemo(
        () => (data?.UnapprovedContentSummaries || []) as SummaryRow[],
        [data],
    );

    const rows = useMemo(() => {
        if (mode === 'history') return allRows;
        const latestDayByProject = new Map<string, string>();
        for (const row of allRows) {
            const current = latestDayByProject.get(row.projectId);
            if (!current || row.day > current) latestDayByProject.set(row.projectId, row.day);
        }
        return allRows.filter(row => row.day === latestDayByProject.get(row.projectId));
    }, [allRows, mode]);

    const groups = useMemo(() => {
        const map = new Map<string, { key: string; label: string; count: number; rows: SummaryRow[]; projects: Set<string> }>();
        for (const row of rows) {
            const key = groupBy === 'family' ? row.familyName : (row.reporterId || row.reporterName || 'unknown');
            const label = groupBy === 'family' ? row.familyName : (row.reporterName || row.reporterId || '(unknown user)');
            const current = map.get(key) || { key, label, count: 0, rows: [], projects: new Set<string>() };
            current.count += row.detectionCount;
            current.rows.push(row);
            current.projects.add(row.projectId);
            map.set(key, current);
        }
        return Array.from(map.values()).sort((a, b) => b.count - a.count);
    }, [rows, groupBy]);

    const totals = useMemo(() => ({
        families: new Set(rows.map(row => row.familyName)).size,
        occurrences: rows.reduce((sum, row) => sum + row.detectionCount, 0),
        projects: new Set(rows.map(row => row.projectId)).size,
    }), [rows]);

    const toggle = (key: string) => setExpanded(previous => {
        const next = new Set(previous);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    return (
        <div className="page-container" style={{ ...style, overflow: 'auto' }} data-testid="TrendingUnapprovedContentReport">
            <div className="page-header">
                <BackHeading title="Trending Unapproved Content" subtitle="Frequency analysis from daily summary data" onBack={onBack} />
                <div className="flex items-center gap-3 flex-wrap">
                    <PeriodSelector value={periodId} onChange={setPeriodId} />
                    <ToggleSelector label="Mode" value={mode} onChange={setMode} options={[
                        { id: 'latest', label: 'Latest day per project' },
                        { id: 'history', label: 'Full period' },
                    ]} />
                    <ToggleSelector label="Group by" value={groupBy} onChange={setGroupBy} options={[
                        { id: 'family', label: 'Family' },
                        { id: 'user', label: 'User' },
                    ]} />
                    <RefreshButton onRefresh={refetch} className="btn-secondary" iconSize={16} />
                </div>
            </div>
            {loading && <LoadingCard />}
            {error && <ErrorCard message={error.message} />}
            {!loading && !error && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-slide-up">
                        {[
                            { label: 'Distinct Families', value: totals.families, icon: Sparkles, colour: 'var(--accent)', background: 'var(--accent-soft)' },
                            { label: 'Total Occurrences', value: totals.occurrences, icon: FileWarning, colour: 'var(--danger)', background: 'var(--danger-soft)' },
                            { label: 'Projects Affected', value: totals.projects, icon: FolderKanban, colour: 'var(--success)', background: 'var(--success-soft)' },
                        ].map(card => (
                            <div className="card p-5" key={card.label}>
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: card.background }}>
                                    <card.icon size={20} style={{ color: card.colour }} />
                                </div>
                                <div className="stat-value">{card.value.toLocaleString()}</div>
                                <div className="stat-label mt-1">{card.label}</div>
                            </div>
                        ))}
                    </div>
                    <div className="card animate-slide-up">
                        {groups.length === 0 ? (
                            <div className="text-sm text-center py-10" style={{ color: 'var(--text-tertiary)' }}>No summary data has been collected in this period.</div>
                        ) : groups.map(group => {
                            const isExpanded = expanded.has(group.key);
                            return (
                                <div key={group.key} style={{ borderBottom: '1px solid var(--border-default)' }}>
                                    <button onClick={() => toggle(group.key)} className="w-full flex items-center gap-3 px-6 py-3 text-left" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        <span className="flex-1 text-sm font-medium">{group.label}</span>
                                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{group.projects.size.toLocaleString()} projects</span>
                                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}>
                                            {group.count.toLocaleString()} occurrences
                                        </span>
                                    </button>
                                    {isExpanded && <DetailTable rows={group.rows} showProject />}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};
